import { useMemo, useState } from 'react'
import type { Enquiry, VisitListResponse } from '@fitting-room/contracts'
import { Link2, Search } from 'lucide-react'
import { DatePicker } from '../../components/admin/DatePicker'
import { Drawer } from '../../components/admin/Drawer'
import { PageHeader } from '../../components/admin/PageHeader'
import { useEnquiries, useLinkEnquiry, useVisits } from '../../lib/adminQueries'
import { formatDay } from '../../lib/format'
import { BOUTIQUE_LOCALE, boutiqueDateTime, boutiqueTime, boutiqueToday } from '../../lib/boutique'
import { useStaffToken } from '../../lib/staffToken'

type VisitRow = VisitListResponse['visits'][number]

/**
 * Why a booking may not be linked to, said in the words of the thing that
 * would go wrong. The API refuses each of these regardless — this only saves
 * the stylist a click and tells her which appointment she is looking at.
 *
 * The open-and-untouched case is not blocked, and that is the case that
 * matters: every ingested appointment is provisioned with a session, so
 * almost every real target has one, and it is nearly always a link nobody has
 * opened. The enquiry supersedes it. `sessionStatus` cannot tell those apart
 * from a rail she is half-way through — both read `open` — which is why the
 * row carries `sessionUntouched` as well.
 */
function blockedBecause(visit: VisitRow): string | null {
  if (visit.sessionStatus === 'submitted') return 'someone has already sent a list for this appointment'
  if (visit.sessionStatus === 'none') return null
  if (visit.sessionUntouched) return null
  return 'somebody has started filling in this appointment’s own link'
}

/** What the session note says when the row can be linked to. */
function sessionNote(visit: VisitRow): string {
  if (visit.sessionStatus === 'none') return 'nothing sent yet'
  // An untouched link is about to be superseded by this enquiry, and the
  // stylist should know that is what her click does.
  return 'link never used — this will replace it'
}

function ContactLines({ enquiry }: { readonly enquiry: Enquiry }) {
  if (enquiry.contact.length === 0) return <span className="st-badge st-badge--alert">no way to reach them</span>
  return (
    <span className="flex flex-wrap gap-x-3 gap-y-0.5">
      {enquiry.contact.map((entry) => (
        <span key={entry.key} className="whitespace-nowrap">
          <span className="st-eyebrow mr-1">{entry.label}</span>
          {entry.value}
        </span>
      ))}
    </span>
  )
}

/**
 * The link action: a day, the appointments on it, and one button.
 *
 * Deliberately not a free-text visit id. The stylist knows the customer and
 * the day, not a `vst_` string, and the day she reaches for first is the one
 * the enquiry claims — so that is what this opens on.
 *
 * It lives in the drawer rather than above the queue: it is about ONE enquiry,
 * and a panel that pushes the whole list down the page loses the row it is
 * asking about.
 */
function LinkDrawer({
  enquiry,
  locale,
  onClose,
}: {
  readonly enquiry: Enquiry
  readonly locale: string
  readonly onClose: () => void
}) {
  const { token } = useStaffToken()
  const [date, setDate] = useState(enquiry.unverifiedBookingClaim?.claimedDate ?? boutiqueToday())
  const visits = useVisits(token, date)
  const mutation = useLinkEnquiry(token, enquiry.sessionId)

  const rows = useMemo(
    () => [...(visits.data?.visits ?? [])].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)),
    [visits.data],
  )

  return (
    <Drawer
      open
      onOpenChange={(next) => (next ? undefined : onClose())}
      eyebrow="Link to an appointment"
      title={enquiry.name?.value ?? 'This inquiry'}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="st-eyebrow">Appointments on</span>
        <DatePicker value={date} onChange={setDate} label="Appointments on" />
      </div>

      {enquiry.unverifiedBookingClaim ? (
        <p className="st-note st-meta mb-4">
          They say they booked for {formatDay(enquiry.unverifiedBookingClaim.claimedDate, locale)}. Nobody has checked.
        </p>
      ) : null}

      {mutation.isError ? (
        <p className="st-alert mb-4">
          {mutation.error.message} <span className="text-xs opacity-70">({mutation.error.code})</span>
        </p>
      ) : null}

      {rows.length === 0 ? (
        <p className="st-meta">{visits.isPending ? 'Loading…' : 'No appointments booked for this date.'}</p>
      ) : null}

      {rows.map((visit) => {
        const blocked = blockedBecause(visit)
        return (
          <div key={visit.id} className="st-card mb-2 px-4 py-3">
            <div className="flex items-baseline justify-between gap-3">
              <div className="flex min-w-0 items-baseline gap-3">
                <span className="st-display shrink-0 tabular-nums">{boutiqueTime(visit.scheduledAt)}</span>
                <span className="truncate font-medium">{visit.customerName ?? 'No name'}</span>
              </div>
              <span className="st-meta shrink-0">{visit.tierKey}</span>
            </div>
            <p className={`mt-1 text-xs ${blocked ? 'st-note' : 'st-meta'}`}>{blocked ?? sessionNote(visit)}</p>
            <button
              type="button"
              className="st-btn mt-3 w-full"
              disabled={Boolean(blocked) || mutation.isPending}
              // Closed here rather than by watching the queue reload: the row
              // leaves the list on the refetch, and a panel that lingers until
              // then reads as a click that did nothing.
              onClick={() => mutation.mutate({ visitId: visit.id }, { onSuccess: onClose })}
            >
              Link to this appointment
            </button>
          </div>
        )
      })}
    </Drawer>
  )
}

function EnquiryCard({
  enquiry,
  locale,
  onLink,
}: {
  readonly enquiry: Enquiry
  readonly locale: string
  readonly onLink: () => void
}) {
  const picks = Object.entries(enquiry.pickCounts)
  // A lead: they told us who they are on the fitting room's first screen and
  // have not booked. Nothing to link yet — the list is still theirs to change —
  // so the card is a name and a number to call, and when they were last in.
  const lead = enquiry.status === 'started'
  return (
    <article className="st-card mb-2 px-4 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="flex items-baseline gap-2 truncate text-base font-medium">
            {enquiry.name?.value ?? <span className="st-meta">no name</span>}
            {lead ? <span className="st-badge">started, not booked</span> : null}
          </h2>
          <p className="st-meta mt-0.5">
            {lead ? 'last in' : 'sent'} {boutiqueDateTime(enquiry.submittedAt)}
          </p>
        </div>
        {lead ? null : (
          <button type="button" className="st-btn-ghost shrink-0" onClick={onLink}>
            <Link2 size={14} aria-hidden />
            Link to appointment
          </button>
        )}
      </div>

      <div className="st-meta mt-2.5 flex flex-col gap-1">
        <ContactLines enquiry={enquiry} />
        {enquiry.details.length > 0 ? (
          <span className="flex flex-wrap gap-x-3 gap-y-0.5">
            {enquiry.details.map((entry) => (
              <span key={entry.key} className="whitespace-nowrap">
                <span className="st-eyebrow mr-1">{entry.label}</span>
                {entry.value}
              </span>
            ))}
          </span>
        ) : null}
        <span className="tabular-nums">
          {picks.length === 0
            ? 'picked nothing'
            : picks.map(([key, count]) => `${key} ${count}`).join(' · ')}
        </span>
      </div>

      {/* Her claim, and the fact that it is only a claim. Nothing has been
          looked up: no visit was searched for on that day and none was found.
          Labelled plainly enough that it can never be mistaken for a match
          already made. */}
      {enquiry.unverifiedBookingClaim ? (
        <p className="st-note st-meta mt-2.5">
          Says they booked for {formatDay(enquiry.unverifiedBookingClaim.claimedDate, locale)} — their words, unverified.
        </p>
      ) : null}
    </article>
  )
}

/**
 * The inquiries: lists somebody finished without an appointment behind them.
 *
 * Linking one sets its `visitId` and nothing else, after which the pull sheet,
 * the day view and the outcomes screen read it like any other session. That is
 * the whole of the mechanism, and it is why the refusals are worth reading:
 * an appointment that already has a sent list is never quietly overwritten.
 */
export function EnquiriesPage() {
  const { token } = useStaffToken()
  const query = useEnquiries(token)
  const [linkingId, setLinkingId] = useState<string | null>(null)
  const [filter, setFilter] = useState('')
  const locale = BOUTIQUE_LOCALE

  const enquiries = query.data?.enquiries ?? []
  const linking = enquiries.find((row) => row.sessionId === linkingId)

  const shown = useMemo(() => {
    const needle = filter.trim().toLowerCase()
    if (!needle) return enquiries
    return enquiries.filter((row) =>
      [row.name?.value ?? '', ...row.contact.map((entry) => entry.value)]
        .join(' ')
        .toLowerCase()
        .includes(needle),
    )
  }, [enquiries, filter])

  return (
    <div>
      <PageHeader eyebrow="Nobody has claimed these" title="Inquiries">
        <label className="relative flex items-center">
          <Search size={14} className="pointer-events-none absolute left-2.5 opacity-50" aria-hidden />
          <input
            className="st-input st-input-search w-64"
            type="search"
            placeholder="Filter by name or contact"
            aria-label="Filter by name or contact"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
          />
        </label>
      </PageHeader>

      <p className="st-meta mb-4 max-w-2xl">
        Lists sent by someone who had no appointment link — attach one to a booking and its gowns become
        that appointment&rsquo;s favorites — and, below them, people who started a fitting room and have not booked
        yet: a name and a number worth a call.
      </p>

      {query.isError ? (
        <p className="st-alert mb-4">Could not load the inquiries. Check the access token and try again.</p>
      ) : null}

      {shown.length === 0 ? (
        <div className="st-card px-4 py-10 text-center">
          <p className="st-meta">
            {query.isPending ? 'Loading…' : enquiries.length === 0 ? 'No inquiries waiting.' : 'Nothing matches that.'}
          </p>
        </div>
      ) : null}

      {shown.map((enquiry) => (
        <EnquiryCard
          key={enquiry.sessionId}
          enquiry={enquiry}
          locale={locale}
          onLink={() => setLinkingId(enquiry.sessionId)}
        />
      ))}

      {linking ? (
        <LinkDrawer key={linking.sessionId} enquiry={linking} locale={locale} onClose={() => setLinkingId(null)} />
      ) : null}
    </div>
  )
}
